import { useCallback, useEffect, useState } from 'react';
import { Lock, Trash2, X } from 'lucide-react';
import { fetchApi } from '../../api';

interface ManageableCategory {
  id: number | null;
  name: string;
  businessType: string;
  isPreset: boolean;
  productCount: number;
}

interface CategoryManagerModalProps {
  businessType: string;
  onClose: () => void;
  onChanged: () => void;
}

/**
 * Category management, including the deletion the backend has always supported
 * and nothing ever called.
 *
 * `Product.category` is a plain string, so deleting a category that is still in
 * use would leave products wearing a label that can no longer be picked. The API
 * refuses that by default; this dialog asks where those products should go and
 * retries with the answer.
 */
export default function CategoryManagerModal({ businessType, onClose, onChanged }: CategoryManagerModalProps) {
  const [categories, setCategories] = useState<ManageableCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchApi(`/categories/manage?businessType=${businessType}`);
      setCategories(Array.isArray(data) ? data : []);
      setError('');
    } catch (e: any) {
      setError(e?.message || 'Не удалось загрузить категории');
    } finally {
      setLoading(false);
    }
  }, [businessType]);

  useEffect(() => { void load(); }, [load]);

  const handleAdd = async (event: React.FormEvent) => {
    event.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await fetchApi('/categories', { method: 'POST', body: JSON.stringify({ businessType, name }) });
      setNewName('');
      setMessage(`Категория «${name}» добавлена`);
      setError('');
      await load();
      onChanged();
    } catch (e: any) {
      setError(e?.message || 'Не удалось добавить категорию');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (category: ManageableCategory) => {
    if (!category.id) return;
    if (!window.confirm(`Удалить категорию «${category.name}»?`)) return;

    setBusy(true);
    setMessage('');
    try {
      await fetchApi(`/categories/${category.id}`, { method: 'DELETE' });
      setMessage(`Категория «${category.name}» удалена`);
      setError('');
      await load();
      onChanged();
      return;
    } catch (e: any) {
      // Still in use. Ask where the products should go rather than orphaning them.
      const reassignTo = await promptReassignTarget(category, categories);
      if (reassignTo === null) {
        setError(e?.message || 'Категория используется товарами');
        return;
      }
      try {
        const result = await fetchApi(
          `/categories/${category.id}?reassignTo=${encodeURIComponent(reassignTo)}`,
          { method: 'DELETE' },
        );
        const moved = Number(result?.movedProducts) || 0;
        setMessage(
          moved > 0
            ? `Категория «${category.name}» удалена, ${moved} товар(ов) перенесено ${reassignTo ? `в «${reassignTo}»` : 'без категории'}`
            : `Категория «${category.name}» удалена`,
        );
        setError('');
        await load();
        onChanged();
      } catch (retryError: any) {
        setError(retryError?.message || 'Не удалось удалить категорию');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleNewNameChange = (event: React.ChangeEvent<HTMLInputElement>) => setNewName(event.target.value);
  const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

  return (
    <div className="modal open category-manager-modal" onClick={onClose}>
      <div className="modal-card category-manager-card" onClick={stopPropagation} role="dialog" aria-modal="true">
        <div className="modal-head">
          <h3>Категории каталога</h3>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Закрыть">
            <X size={16} />
          </button>
        </div>

        <form className="category-add-form" onSubmit={handleAdd}>
          <input
            type="text"
            placeholder="Новая категория…"
            value={newName}
            onChange={handleNewNameChange}
            aria-label="Название новой категории"
          />
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !newName.trim()}>
            Добавить
          </button>
        </form>

        {message && <div className="category-manager-notice">{message}</div>}
        {error && <div className="category-manager-error" role="alert">{error}</div>}

        {loading ? (
          <div className="category-manager-empty">Загрузка категорий…</div>
        ) : (
          <ul className="category-manager-list">
            {categories.map((category) => (
              <li key={`${category.businessType}-${category.name}`} className="category-manager-row">
                <span className="category-manager-name">{category.name}</span>
                <span className="pill gray">{category.productCount} товар(ов)</span>
                {category.isPreset ? (
                  <span className="category-manager-preset" title="Встроенная категория — её нельзя удалить">
                    <Lock size={12} /> встроенная
                  </span>
                ) : (
                  <button
                    type="button"
                    className="row-del"
                    onClick={() => handleDelete(category)}
                    disabled={busy}
                    title={`Удалить категорию ${category.name}`}
                    aria-label={`Удалить категорию ${category.name}`}
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * Ask where the products of a category in use should move. Returns the target
 * name (empty string = clear the category) or null when the operator backs out.
 */
async function promptReassignTarget(
  category: ManageableCategory,
  categories: ManageableCategory[],
): Promise<string | null> {
  const alternatives = categories
    .filter((candidate) => candidate.name !== category.name)
    .map((candidate) => candidate.name);

  const answer = window.prompt(
    `Категория «${category.name}» используется в ${category.productCount} товар(ах).\n\n` +
      'Введите название категории, в которую их перенести, или оставьте поле пустым, ' +
      'чтобы очистить у них категорию.\n\n' +
      (alternatives.length > 0 ? `Доступные: ${alternatives.join(', ')}` : ''),
    '',
  );

  return answer === null ? null : answer.trim();
}
